import { getApiBaseUrl } from '@/config/runtimeUrls';
import { CELL_SCOPE_HEADER, readCellScope } from '@/services/cellScope';

const AUTH_TOKEN_KEYS = ['auth_token', 'auth_v2_accessToken', 'accessToken', 'authToken', 'token'];
const REQUEST_TIMEOUT_MS = 30_000;

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const key of AUTH_TOKEN_KEYS) {
    const value = String(window.localStorage.getItem(key) || '').trim();
    if (value && value.split('.').length >= 3) return value;
  }
  return '';
};

const fetchWithTimeout = async (url: string, init: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const extractErrorMessage = (payload: any, fallback: string): string =>
  String(
    payload?.error?.message ||
      payload?.message ||
      payload?.data?.message ||
      payload?.data?.error ||
      payload?.error ||
      fallback
  );

export const requestAuthHandoff = async (
  targetHost: string,
  returnPath: string
): Promise<{ redirectUrl: string; tokenExpiresAt?: string }> => {
  const token = readAccessToken();
  if (!token) {
    throw new Error('Session token is missing. Please sign in again.');
  }

  const response = await fetchWithTimeout(`${getApiBaseUrl()}/auth/handoff`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      [CELL_SCOPE_HEADER]: readCellScope(),
    },
    body: JSON.stringify({
      targetHost,
      returnPath,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  const data = payload?.data ?? payload;
  if (!response.ok || payload?.success === false || !data?.redirectUrl) {
    throw new Error(extractErrorMessage(payload, 'Unable to continue to the selected surface.'));
  }

  return {
    redirectUrl: String(data.redirectUrl),
    tokenExpiresAt: data.tokenExpiresAt ? String(data.tokenExpiresAt) : undefined,
  };
};
